/** Web Mercator tile math. The canvas grid is the same quadtree, so this is display and storage both. */

export interface LngLatBounds {
  west: number;
  south: number;
  east: number;
  north: number;
}

const RAD = 180 / Math.PI;

export function tileToBounds(z: number, x: number, y: number): LngLatBounds {
  const n = Math.pow(2, z);
  const west = (x / n) * 360 - 180;
  const east = ((x + 1) / n) * 360 - 180;
  const north = Math.atan(Math.sinh(Math.PI * (1 - (2 * y) / n))) * RAD;
  const south = Math.atan(Math.sinh(Math.PI * (1 - (2 * (y + 1)) / n))) * RAD;
  return { west, south, east, north };
}

/** Longitude of pixel column `px` and latitude of pixel row `py` of a tile (pixel centres). */
export function tilePixelLng(z: number, x: number, px: number, size: number): number {
  const n = Math.pow(2, z);
  return ((x + (px + 0.5) / size) / n) * 360 - 180;
}

export function tilePixelLat(z: number, y: number, py: number, size: number): number {
  const n = Math.pow(2, z);
  return Math.atan(Math.sinh(Math.PI * (1 - (2 * (y + (py + 0.5) / size)) / n))) * RAD;
}

/** Approximate ground resolution of a tile pixel (m/px) at latitude. */
export function metersPerPixel(z: number, lat: number, size = 256): number {
  return (156543.03392 * Math.cos((lat * Math.PI) / 180)) / Math.pow(2, z) / (size / 256);
}

/** Zoom thresholds for the rendering modes (these are source tile zooms). */
export const FULL_RES_MIN_ZOOM = 15;
export const TILE_SUMMARY_MIN_ZOOM = 11;

export type RenderMode = 'full' | 'tile' | 'cell';

export function modeForZoom(z: number): RenderMode {
  if (z >= FULL_RES_MIN_ZOOM) return 'full';
  if (z >= TILE_SUMMARY_MIN_ZOOM) return 'tile';
  return 'cell';
}
